/**
 * Illustrative NextOnVera clinician dashboard for the product hero.
 * Layout-matched to the public mock; demo roster only — not live patient data.
 */
import {
  ActivityIcon,
  BrainIcon,
  ClipboardCheckIcon,
  DatabaseIcon,
  HeartHandshakeIcon,
  LayoutDashboardIcon,
  LogOutIcon,
  PillIcon,
  SettingsIcon,
  SparklesIcon,
  UsersIcon,
  type LucideIcon
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { assetUrl } from '@/lib/asset-url';

const NEXTONVERA_MARK_SRC = 'brand/nextonvera-logo-mark.png';

const navItems: { label: string; icon: LucideIcon; active?: boolean }[] = [
  { label: 'Dashboard', icon: LayoutDashboardIcon, active: true },
  { label: 'Patients', icon: UsersIcon },
  { label: 'Medications', icon: PillIcon },
  { label: 'Research Data', icon: DatabaseIcon },
  { label: 'Evidence Review', icon: ClipboardCheckIcon },
  { label: 'AI Analysis', icon: BrainIcon },
  { label: 'GraceAI', icon: SparklesIcon },
  { label: 'Patient Support', icon: HeartHandshakeIcon },
  { label: 'Administration', icon: SettingsIcon }
];

const stats = [
  { label: 'Total Patients', value: '29', icon: UsersIcon, iconClass: 'text-sky-500 bg-sky-50' },
  { label: 'Targeted Medications', value: '201', icon: PillIcon, iconClass: 'text-teal-600 bg-teal-50' },
  { label: 'Research Datasets', value: '27565', icon: DatabaseIcon, iconClass: 'text-violet-600 bg-violet-50' },
  { label: 'Tracked Biomarkers', value: '232', icon: ActivityIcon, iconClass: 'text-rose-500 bg-rose-50' }
] as const;

const biomarkers = [
  { label: 'MGMT', value: 95 },
  { label: 'EGFR', value: 80 },
  { label: 'IDH1', value: 42 },
  { label: 'PD-L1', value: 26 },
  { label: 'PTEN', value: 22 },
  { label: 'EGFRvIII', value: 21 },
  { label: 'TERT', value: 18 }
] as const;

const recentPatients = [
  { name: 'Chuck Norris', detail: 'Glioblastoma', stage: 'Chuck Norris (CNS WHO 2026)' },
  { name: 'Jane Doe11', detail: 'Glioma', stage: 'Stage WHO Grade 4 (CNS WHO 2021)' },
  { name: 'Tom Jones', detail: 'Glioma', stage: 'Stage WHO Grade 2 (CNS WHO 2021)' },
  { name: 'GBM-007 Patient', detail: 'Glioma', stage: 'Stage WHO Grade 4 (CNS WHO 2021)' },
  { name: 'ASTRO-001 Patient', detail: 'Glioma', stage: 'Stage WHO Grade 3 (CNS WHO 2021)' }
] as const;

export function NextOnVeraDashboardPreview() {
  return (
    <div className="bg-[#eef2f7] text-slate-800 overflow-hidden rounded-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.8)] ring-1 ring-slate-200/90">
      <div className="flex items-center gap-2 border-b border-slate-200/80 bg-white px-3 py-2 sm:px-3.5">
        <img
          src={assetUrl(NEXTONVERA_MARK_SRC)}
          alt=""
          aria-hidden
          className="size-6 shrink-0 object-contain"
        />
        <p className="text-[13px] font-semibold tracking-tight text-slate-800">NextOnVera</p>
        <p className="text-slate-400 hidden text-[10px] sm:inline">Proudly led by</p>
        <span className="inline-flex items-center gap-1 rounded-full bg-[#f5c400] px-2 py-0.5 text-[9px] font-bold tracking-wide text-slate-900 uppercase">
          Tour de Cure
        </span>
        <span className="rounded-full bg-[#f5c400]/90 px-2 py-0.5 text-[9px] font-semibold tracking-wide text-slate-800 uppercase">
          Live trial
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          <span className="inline-flex items-center rounded-full bg-slate-800 px-2.5 py-1 text-[10px] font-medium text-white">
            Dashboard
          </span>
          <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-medium text-slate-600">
            <LogOutIcon className="size-2.5" aria-hidden />
            datito
          </span>
        </div>
      </div>

      <div className="flex min-h-0">
        <aside className="hidden w-[9.25rem] shrink-0 flex-col gap-0.5 border-r border-slate-200/80 bg-white px-2 py-3 sm:flex">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.label}
                className={cn(
                  'flex items-center gap-2 rounded-lg px-2 py-1.5 text-[11px]',
                  item.active
                    ? 'bg-sky-50 font-semibold text-sky-700'
                    : 'text-slate-500'
                )}
              >
                <Icon className="size-3.5 shrink-0" aria-hidden />
                <span className="truncate">{item.label}</span>
              </div>
            );
          })}
        </aside>

        <div className="min-w-0 flex-1 space-y-3 p-3 sm:p-4">
          <div>
            <h2 className="text-[15px] font-semibold tracking-tight text-slate-800 sm:text-base">
              Dashboard Overview
            </h2>
            <p className="text-slate-500 mt-0.5 text-[11px] leading-snug">
              Real-time statistics across the research platform.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            {stats.map((stat) => {
              const Icon = stat.icon;
              return (
                <div
                  key={stat.label}
                  className="flex items-center justify-between gap-2 rounded-xl border border-slate-200/80 bg-white px-3 py-2.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
                >
                  <div className="min-w-0">
                    <p className="text-slate-500 truncate text-[10px] leading-tight">{stat.label}</p>
                    <p className="text-[1.35rem] font-semibold tracking-tight tabular-nums text-slate-800">
                      {stat.value}
                    </p>
                  </div>
                  <span className={cn('flex size-8 shrink-0 items-center justify-center rounded-lg', stat.iconClass)}>
                    <Icon className="size-4" aria-hidden />
                  </span>
                </div>
              );
            })}
          </div>

          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_13.5rem]">
            <div className="rounded-xl border border-slate-200/80 bg-white p-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              <p className="text-[12px] font-semibold text-slate-800">Most Prevalent Biomarkers</p>
              <div className="mt-3 flex h-36 gap-2 sm:h-40">
                <div className="text-slate-400 flex h-full flex-col justify-between py-0.5 text-[9px] tabular-nums">
                  <span>2000</span>
                  <span>1500</span>
                  <span>1000</span>
                  <span>500</span>
                  <span>0</span>
                </div>
                <div className="flex min-w-0 flex-1 items-end justify-between gap-1 border-l border-slate-100 pl-2">
                  {biomarkers.map((marker) => (
                    <div key={marker.label} className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1">
                      <div
                        className="w-full max-w-7 rounded-t-sm bg-[#1e4f8c]"
                        style={{ height: `${marker.value}%` }}
                      />
                      <span className="text-slate-500 w-full truncate text-center text-[9px] leading-none">
                        {marker.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200/80 bg-white p-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-[12px] font-semibold text-slate-800">Recent Patients</p>
                <span className="text-[10px] font-medium text-sky-600">View All ›</span>
              </div>
              <ul className="space-y-2">
                {recentPatients.map((patient) => (
                  <li key={patient.name} className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-[11px] font-semibold text-slate-800">{patient.name}</p>
                      <p className="text-slate-400 truncate text-[10px]">{patient.detail}</p>
                    </div>
                    <span className="shrink-0 rounded-full bg-sky-50 px-2 py-0.5 text-[8px] leading-tight font-medium text-sky-700">
                      {patient.stage}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
